/**
 * Torg Pricing in the PDP buy box (contract §7; legal display rules in
 * .docs/torg-search-migration/22-dfp-forpackning.md §Juridik).
 *
 * The server renders the block for the shown product
 * (versa_torg_pricing_buy_box() in app/helpers/pdp-buy-box.php — keep the
 * markup below in step with it). This module
 * - re-renders it for the chosen variation (found_variation →
 *   variation.versa_torg_tiers, falling back to the plugin's torg_tiers);
 * - re-renders it on edge-state hydration (window.VersaTorgPricing.hydrate,
 *   called by mu-plugins/versa-pdp-edge/assets/pdp-state.js).
 *
 * Packs are a "Förpackning" price list: "10 st", grey "(20 kr/st)", the pack
 * total and an add button posting data-quantity N and data-torg_pack N
 * (add-to-cart.js). The price row, stepper and CTA always mean single units.
 */

const ROOT = '[data-versa-torg-pricing]';

function esc(value) {
    return String(value === undefined || value === null ? '' : value).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
}

/** wc_price() for tiers that arrive without price HTML (plugin's raw torg_tiers). */
function formatPrice(root, amount) {
    let cfg = {};
    try {
        cfg = JSON.parse(root.dataset.versaTorgCurrency || '{}') || {};
    } catch (e) {
        cfg = {};
    }
    const decimals = Number.isInteger(cfg.decimals) ? cfg.decimals : 2;
    const fixed = Number(amount).toFixed(decimals).split('.');
    const whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, cfg.thousand_separator || '');
    let number = whole;
    if (fixed[1] && !(cfg.trim_zeros && /^0+$/.test(fixed[1]))) {
        number = whole + (cfg.decimal_separator || ',') + fixed[1];
    }
    const symbol = '<span class="woocommerce-Price-currencySymbol">' + esc(cfg.symbol || '') + '</span>';
    const format = cfg.format || '%2$s&nbsp;%1$s';
    return '<span class="woocommerce-Price-amount amount"><bdi>' + format.replace('%1$s', symbol).replace('%2$s', number) + '</bdi></span>';
}

/**
 * Tiers in the shape the markup needs. Theme-enriched tiers (versa_torg_tiers,
 * PHP versa_torg_pricing_tiers()) carry price HTML and stock limits; the
 * plugin's raw torg_tiers are filtered here the same way (below the current
 * price, packs only when the stock covers one).
 */
function normaliseTiers(root, list, limits) {
    limits = limits || {};
    const maxQty = Number(limits.maxQty) > 0 ? Number(limits.maxQty) : -1;
    const current = Number(limits.displayPrice) || 0;
    return (Array.isArray(list) ? list : []).map((tier) => {
        if (!tier || (tier.mode !== 'pack' && tier.mode !== 'quantity')) return null;
        const min = parseInt(tier.min_qty, 10);
        const unit = Number(tier.unit_price_display);
        if (!(min >= 2) || !(unit > 0)) return null;
        const enriched = tier.unit_html !== undefined;
        if (!enriched && current > 0 && unit >= current - 0.004) return null;
        let maxPacks = 0;
        if (tier.mode === 'pack') {
            maxPacks = enriched ? parseInt(tier.max_packs, 10) : (maxQty > 0 ? Math.floor(maxQty / min) : -1);
            if (isNaN(maxPacks)) maxPacks = -1;
            if (maxPacks === 0) return null;
        }
        const total = Number(tier.total_display) > 0 ? Number(tier.total_display) : unit * min;
        return {
            mode: tier.mode,
            min_qty: min,
            unit_html: enriched ? tier.unit_html : formatPrice(root, unit),
            total_html: tier.total_html !== undefined ? tier.total_html : formatPrice(root, total),
            max_packs: maxPacks,
            available: enriched ? tier.available !== false : (maxQty < 0 || maxQty >= min),
        };
    }).filter(Boolean).sort((a, b) => (a.mode === b.mode ? a.min_qty - b.min_qty : (a.mode === 'pack' ? -1 : 1)));
}

const PLUS_ICONS = '<svg class="add_to_cart_button_initial h-4 w-4 pointer-events-none" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v14M5 12h14"/></svg>'
    + '<svg class="hidden add_to_cart_button_loading animate-spin h-4 w-4 pointer-events-none" aria-hidden="true" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>'
    + '<svg class="hidden add_to_cart_button_added h-4 w-4 pointer-events-none" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>';

function packRowHtml(tier, ctx) {
    const qty = tier.min_qty;
    return `<li class="flex items-center gap-2 px-3 py-2 text-sm">
        <span class="font-medium text-gray-900">${qty} st</span>
        <span class="min-w-0 text-gray-500">(${tier.unit_html}/st)</span>
        <span class="ml-auto shrink-0 font-semibold text-gray-900">${tier.total_html}</span>
        <button type="button" data-quantity="${qty}" data-torg_pack="${qty}" data-product_id="${esc(ctx.productId)}" data-variation_id="${esc(ctx.variationId || 0)}" data-product_name="${esc(ctx.productName)}" aria-label="${esc('Lägg ' + qty + ' st i varukorgen')}"
            class="add_to_cart_button versa-tier-add ml-1 h-8 w-10 shrink-0 inline-flex items-center justify-center rounded-md border border-versa-700 bg-white text-versa-700 hover:bg-versa-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-versa-700">${PLUS_ICONS}</button>
    </li>`;
}

function rowHtml(tier, ctx) {
    const qty = tier.min_qty;
    return `<li class="flex items-center gap-3 px-3 py-2 text-sm">
        <span class="w-14 shrink-0 text-gray-600">${qty}+ st</span>
        <span class="flex-1 min-w-0"><span class="font-semibold text-gray-900">${tier.unit_html}</span><span class="text-gray-500">/st</span></span>
        <button type="button" data-quantity="${qty}" data-product_id="${esc(ctx.productId)}" data-variation_id="${esc(ctx.variationId || 0)}" data-product_name="${esc(ctx.productName)}" aria-label="${esc('Lägg ' + qty + ' st i varukorgen')}"${tier.available ? '' : ' disabled'}
            class="add_to_cart_button versa-tier-add h-8 shrink-0 inline-flex items-center justify-center gap-1.5 rounded-md border border-versa-700 bg-white px-2.5 text-sm font-medium text-versa-700 hover:bg-versa-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-versa-700 disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-white">
            <svg class="add_to_cart_button_initial h-4 w-4 pointer-events-none" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>
            <svg class="hidden add_to_cart_button_loading animate-spin h-4 w-4 pointer-events-none" aria-hidden="true" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
            <svg class="hidden add_to_cart_button_added h-4 w-4 pointer-events-none" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
            <span class="add_to_cart_button_label pointer-events-none">${qty} st</span>
            <span class="add_to_cart_button_label_adding hidden pointer-events-none">${qty} st</span>
            <span class="add_to_cart_button_label_added hidden pointer-events-none">${qty} st</span>
        </button>
    </li>`;
}

/** Same markup as versa_torg_pricing_inner_html(). */
function render(root, tiers, variationId) {
    const ctx = {
        productId: root.dataset.productId || '',
        variationId: variationId || 0,
        productName: root.dataset.productName || '',
    };
    const packs = tiers.filter((t) => t.mode === 'pack');
    const quantities = tiers.filter((t) => t.mode === 'quantity');
    const labelId = 'versa-torg-pack-label-' + ctx.productId;
    let html = '';
    if (packs.length) {
        html += `<div data-versa-torg-pack><p class="m-0 mb-2 text-sm text-gray-900" id="${esc(labelId)}">Förpackning</p>`
            + `<ul class="m-0 p-0 list-none divide-y divide-gray-100 rounded-lg border border-gray-200" aria-labelledby="${esc(labelId)}">`
            + packs.map((t) => packRowHtml(t, ctx)).join('')
            + '</ul></div>';
    }
    if (quantities.length) {
        html += '<div class="versa-tiers" data-versa-torg-tiers><p class="m-0 mb-2 text-sm text-gray-900">Antal</p>'
            + '<ul class="m-0 p-0 list-none divide-y divide-gray-100 rounded-lg border border-gray-200">'
            + quantities.map((t) => rowHtml(t, ctx)).join('')
            + '</ul></div>';
    }
    root.dataset.variationId = String(ctx.variationId);
    root.innerHTML = html;
    root.hidden = html === '';
    if (html !== '' && typeof window.observeAddToCartButtons === 'function') {
        window.observeAddToCartButtons(false);
    }
}

/** Variation picked: its tiers. */
function onFoundVariation(event, variation) {
    const form = event && event.target && event.target.closest ? event.target.closest('form') : null;
    const root = (form && form.querySelector(ROOT)) || document.querySelector(ROOT);
    if (!root || !variation) return;
    const list = Array.isArray(variation.versa_torg_tiers) ? variation.versa_torg_tiers : variation.torg_tiers;
    const tiers = normaliseTiers(root, list, { maxQty: variation.max_qty, displayPrice: variation.display_price });
    render(root, tiers, variation.variation_id);
    toggleAwdrTable(tiers.some((t) => t.mode === 'quantity'));
}

function onVariationReset(event) {
    const form = event && event.target && event.target.closest ? event.target.closest('form') : null;
    const root = (form && form.querySelector(ROOT)) || document.querySelector(ROOT);
    if (!root) return;
    render(root, [], 0);
    toggleAwdrTable(false);
}

/** Never show the AWDR "Antal" table next to Torg quantity tiers. */
function toggleAwdrTable(hide) {
    document.querySelectorAll('[data-versa-awdr-tiers]').forEach((el) => {
        el.hidden = !!hide;
    });
}

/** Edge-state hydration (pdp-state.js): fresh tiers for the customer's currency and stock. */
function hydrate(product) {
    const root = document.querySelector(ROOT);
    if (!root || !product) return;
    render(root, normaliseTiers(root, product.torg_tiers), 0);
}

window.VersaTorgPricing = { hydrate };

if (window.jQuery) {
    window.jQuery(document)
        .on('found_variation', 'form.variations_form', onFoundVariation)
        .on('reset_data hide_variation', 'form.variations_form', onVariationReset);
}
